import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, Users } from "@phosphor-icons/react";

export default function BoxView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({ company_name: "US Operations" });
  const [contrib, setContrib] = useState(null);

  useEffect(() => {
    (async () => {
      const [b, s] = await Promise.all([api.get(`/trace/box/${id}`), api.get("/settings")]);
      setData(b.data);
      setSettings(s.data);
      // Load worker contributions for the parent run (batch-level attribution).
      const runId = b.data?.assembly_run?.id || b.data?.box?.assembly_run_id;
      if (runId) {
        try {
          const c = await api.get(`/assembly-runs/${runId}/contributions`);
          setContrib(c.data);
        } catch { /* not fatal */ }
      }
    })();
  }, [id]);

  if (!data) return <div className="font-display uppercase">Loading...</div>;
  const { box, packing_list, assembly_run } = data;
  // For new boxes (assembly-run created), trace flows through the run's components_consumed
  const lineage = assembly_run?.components_consumed?.length
    ? assembly_run.components_consumed
    : (box.components || []);
  const traceUrl = `${window.location.origin}/trace?box=${box.box_number}`;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="box-view-page">
      <div className="flex items-center justify-between no-print">
        <Link to="/boxes" className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back
        </Link>
        <button onClick={() => window.print()} data-testid="print-box-btn" className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2">
          <Printer size={14} weight="bold" /> Print Box Slip
        </button>
        <Link to={`/boxes/${box.id}/label`} className="brut-btn px-4 py-2 inline-flex items-center gap-2" data-testid="open-label-btn">
          <Printer size={14} weight="bold" /> 4×6 Label
        </Link>
      </div>

      {/* BOX SLIP - barcoded slip */}
      <div className="brut-card p-8 print-section" data-testid="box-slip">
        <div className="flex justify-between items-start border-b-2 border-[#334155] pb-4">
          <div className="flex items-start gap-3">
            <img src="/logo-gate.jpg" alt="GATE" className="w-16 h-16 brut-border object-cover shrink-0" />
            <div>
              <div className="brut-tag brut-tag-yellow mb-1">Box Slip · Traceability</div>
              <div className="font-display text-3xl uppercase tracking-tighter">{settings.company_name}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="brut-label">Box Number</div>
            <div className="font-display text-3xl">{box.box_number}</div>
            <div className="font-mono-ui text-xs mt-1">{new Date(box.created_at).toLocaleString()}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-5">
          <div className="brut-border p-3 col-span-2">
            <div className="brut-label">Product (Fully Assembled)</div>
            <div className="font-mono-ui text-sm font-bold">{box.product_part_number || "—"}</div>
            <div className="font-display text-xl uppercase">{box.product_name}</div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <div className="brut-label">Units in Box</div>
                <div className="font-display text-2xl">{box.units_count}</div>
              </div>
              <div>
                <div className="brut-label">Assembled By</div>
                <div className="font-mono-ui text-sm">{box.created_by_name}</div>
              </div>
              <div>
                <div className="brut-label">Packing List</div>
                <div className="font-mono-ui text-sm">{box.packing_list_number || "—"}</div>
              </div>
              <div>
                <div className="brut-label">Components</div>
                <div className="font-mono-ui text-sm">{lineage.length}</div>
              </div>
              {box.assembly_run_number && (
                <div>
                  <div className="brut-label">Assembly Run</div>
                  <div className="font-mono-ui text-sm font-bold">{box.assembly_run_number}</div>
                </div>
              )}
            </div>
          </div>
          <div className="brut-border p-3 flex flex-col items-center justify-center bg-white">
            <QRCodeSVG value={traceUrl} size={150} bgColor="#fff" fgColor="#1e293b" level="M" />
            <div className="brut-label mt-2 text-center">{box.box_number}</div>
            <div className="text-[9px] text-zinc-500 mt-1 text-center">Scan for traceability</div>
          </div>
        </div>

        <div className="mt-5">
          <div className="brut-label mb-2">Components & Route to Suppliers</div>
          <table className="brut-table">
            <thead>
              <tr><th>#</th><th>Part #</th><th>Description</th><th>Category</th><th>Qty</th><th>Batch (GRN)</th><th>Supplier</th><th>Received</th></tr>
            </thead>
            <tbody>
              {lineage.map((c, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td className="font-bold">{c.part_number}</td>
                  <td>{c.part_name}</td>
                  <td><span className="brut-tag brut-tag-yellow">{c.part_category}</span></td>
                  <td className="font-mono-ui">{c.qty} {c.unit}</td>
                  <td className="font-mono-ui">{c.grn_number}</td>
                  <td>{c.supplier_name}</td>
                  <td className="font-mono-ui">{c.received_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {box.remarks && (
          <div className="mt-4 brut-border p-3">
            <div className="brut-label">Remarks</div>
            <div className="text-sm">{box.remarks}</div>
          </div>
        )}

        {contrib && contrib.rows && contrib.rows.length > 0 && (
          <div className="mt-5" data-testid="box-built-by">
            <div className="brut-label mb-2 inline-flex items-center gap-2"><Users size={12} weight="bold" /> Built By (batch-level attribution)</div>
            <div className="text-[10px] text-zinc-500 mb-2">
              These workers contributed to Assembly Run <b>{box.assembly_run_number}</b> during its build window. Attribution is at the run level — this box is one of {contrib.rows.length > 0 ? "several" : "the"} boxes produced from that batch.
            </div>
            <table className="brut-table">
              <thead><tr><th>Worker</th><th>Role</th><th>Operations</th><th>Days</th><th className="text-right">Pieces</th><th className="text-right">Share</th></tr></thead>
              <tbody>
                {contrib.rows.map((r) => (
                  <tr key={r.staff_id}>
                    <td className="font-bold">{r.staff_name}</td>
                    <td><span className="brut-tag brut-tag-yellow text-[10px]">{r.designation}</span></td>
                    <td className="font-mono-ui text-xs">{r.operations.join(", ")}</td>
                    <td className="font-mono-ui text-xs">{r.sessions.length}</td>
                    <td className="text-right font-mono-ui font-bold">{r.produced}</td>
                    <td className="text-right font-mono-ui">{contrib.cumulative > 0 ? Math.round((r.produced / contrib.cumulative) * 100) + "%" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {packing_list && (
          <div className="mt-4 brut-border p-3 bg-zinc-100">
            <div className="brut-label">Packed in Shipment</div>
            <div className="text-sm font-mono-ui">{packing_list.pl_number} · {packing_list.customer} · Ship date {packing_list.ship_date}</div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 gap-6 text-xs">
          {["Assembled By", "Quality Check"].map((s) => (
            <div key={s} className="border-t-2 border-[#334155] pt-2 text-center">
              <div className="brut-label">{s}</div>
              <div className="h-12" />
              <div className="text-zinc-500">Signature</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
